import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  FolderKanban,
  MoreHorizontal,
  Star,
  Building2,
} from 'lucide-react';

import type { Favorite, FavoriteItemType } from '@/features/favorites/api';
import {
  useFavorites,
  useRemoveFavorite,
  useReorderFavorites,
} from '@/features/favorites/hooks';
import { track } from '@/lib/telemetry';
import { cn } from '@/lib/utils';

export const SIDEBAR_FAVORITES_LIMIT = 6;

const TYPE_ICON: Record<
  FavoriteItemType,
  React.ComponentType<{ className?: string }>
> = {
  workspace: Building2,
  project: FolderKanban,
  dashboard: LayoutDashboard,
};

const TYPE_LABEL: Record<FavoriteItemType, string> = {
  workspace: 'Workspace',
  project: 'Project',
  dashboard: 'Dashboard',
};

function computeMenuStyle(
  anchor: HTMLElement,
  preferredWidth: number,
): CSSProperties {
  const rect = anchor.getBoundingClientRect();
  const gap = 8;
  const edge = 12;
  const vw = window.innerWidth;
  let width = Math.min(preferredWidth, vw - edge * 2);
  let left = rect.right + gap;
  const top = rect.bottom + gap;
  if (left + width > vw - edge) {
    left = Math.max(edge, vw - width - edge);
  }
  return {
    position: 'fixed',
    top,
    left,
    width,
    zIndex: 200,
    maxHeight: 'min(70vh, calc(100vh - 24px))',
  };
}

function FixedMenuPortal({
  open,
  anchorRef,
  preferredWidth,
  testId,
  children,
  onClose,
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  preferredWidth: number;
  testId?: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});

  const update = useCallback(() => {
    if (!open || !anchorRef.current) return;
    setStyle(computeMenuStyle(anchorRef.current, preferredWidth));
  }, [open, anchorRef, preferredWidth]);

  useLayoutEffect(() => {
    update();
  }, [update]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        anchorRef.current?.focus();
      }
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, update, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      style={style}
      role="menu"
      data-testid={testId}
      className="overflow-hidden overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
    >
      {children}
    </div>,
    document.body,
  );
}

function MenuItem({
  label,
  onClick,
  disabled,
  testId,
  danger,
  ariaLabel,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  danger?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        'flex w-full items-center px-3 py-2 text-left text-sm transition',
        danger
          ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30'
          : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      {label}
    </button>
  );
}

function FavoriteRow({
  favorite,
  index,
  total,
  allIds,
  pending,
}: {
  favorite: Favorite;
  index: number;
  total: number;
  allIds: string[];
  pending: boolean;
}) {
  const navigate = useNavigate();
  const remove = useRemoveFavorite();
  const reorder = useReorderFavorites();
  const moreRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const Icon = TYPE_ICON[favorite.itemType];
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const onlyOne = total === 1;

  const openHref = () => {
    track('favorites.open', {
      itemType: favorite.itemType,
      favoriteId: favorite.favoriteId,
    });
    navigate(favorite.href);
  };

  const move = (direction: -1 | 1) => {
    const next = [...allIds];
    const from = next.indexOf(favorite.favoriteId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= next.length) return;
    const tmp = next[from];
    next[from] = next[to]!;
    next[to] = tmp!;
    setMenuOpen(false);
    reorder.mutate(next);
  };

  return (
    <div
      data-sb-row
      className="group relative flex items-start gap-1 rounded-lg px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/60"
      data-testid={`favorite-item-${favorite.favoriteId}`}
    >
      <button
        type="button"
        onClick={openHref}
        className="flex min-w-0 flex-1 items-start gap-2 text-left"
        data-testid={`favorite-open-${favorite.favoriteId}`}
      >
        <Star
          className="mt-0.5 h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500"
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
            <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {favorite.title}
            </span>
          </span>
          {favorite.parentLabel ? (
            <span className="mt-0.5 block truncate text-[11px] text-slate-500">
              {favorite.parentLabel}
              <span className="text-slate-400"> · {TYPE_LABEL[favorite.itemType]}</span>
            </span>
          ) : (
            <span className="mt-0.5 block truncate text-[11px] text-slate-500">
              {TYPE_LABEL[favorite.itemType]}
            </span>
          )}
        </span>
      </button>

      <button
        ref={moreRef}
        type="button"
        data-testid={`favorite-row-more-${favorite.favoriteId}`}
        aria-label={`Actions for ${favorite.title}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={`Actions for ${favorite.title}`}
        disabled={pending}
        className="shrink-0 rounded-md p-1 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 dark:hover:bg-slate-800"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
      >
        <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
      </button>

      <FixedMenuPortal
        open={menuOpen}
        anchorRef={moreRef}
        preferredWidth={200}
        testId={`favorite-row-menu-${favorite.favoriteId}`}
        onClose={() => setMenuOpen(false)}
      >
        <MenuItem
          label="Open"
          testId={`favorite-menu-open-${favorite.favoriteId}`}
          disabled={pending}
          onClick={() => {
            setMenuOpen(false);
            openHref();
          }}
        />
        {!onlyOne && !isFirst ? (
          <MenuItem
            label="Move up"
            testId={`favorite-menu-up-${favorite.favoriteId}`}
            disabled={pending}
            onClick={() => move(-1)}
          />
        ) : null}
        {!onlyOne && !isLast ? (
          <MenuItem
            label="Move down"
            testId={`favorite-menu-down-${favorite.favoriteId}`}
            disabled={pending}
            onClick={() => move(1)}
          />
        ) : null}
        <MenuItem
          label="Remove from favorites"
          ariaLabel={`Remove ${favorite.title} from favorites only — does not delete the source ${TYPE_LABEL[favorite.itemType].toLowerCase()}`}
          testId={`favorite-remove-${favorite.favoriteId}`}
          danger
          disabled={pending}
          onClick={() => {
            setMenuOpen(false);
            remove.mutate({ favoriteId: favorite.favoriteId });
          }}
        />
      </FixedMenuPortal>
    </div>
  );
}

export function FavoritesSidebarSection({
  expanded,
  onToggleExpanded,
}: {
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const navigate = useNavigate();
  const { data: favorites, isLoading, isError, refetch, isFetching } =
    useFavorites();
  const remove = useRemoveFavorite();
  const reorder = useReorderFavorites();
  const sectionMoreRef = useRef<HTMLButtonElement>(null);
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);

  const list = favorites ?? [];
  const visible = list.slice(0, SIDEBAR_FAVORITES_LIMIT);
  const hasMore = list.length > SIDEBAR_FAVORITES_LIMIT;
  const allIds = list.map((f) => f.favoriteId);
  const pending = remove.isPending || reorder.isPending;

  const goAll = () => {
    setSectionMenuOpen(false);
    navigate('/favorites');
  };

  return (
    <div data-testid="section-favorites">
      <div className="group/fav-header flex items-center gap-0.5 px-1">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex min-w-0 flex-1 items-center gap-1 rounded-md px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
          data-testid="section-favorites-toggle"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )}
          Favorites
        </button>
        <button
          ref={sectionMoreRef}
          type="button"
          data-testid="section-favorites-more"
          aria-label="Favorites section menu"
          aria-haspopup="menu"
          aria-expanded={sectionMenuOpen}
          title="Favorites section menu"
          className="rounded-md p-1 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover/fav-header:opacity-100 group-focus-within/fav-header:opacity-100 dark:hover:bg-slate-800"
          onClick={() => setSectionMenuOpen((v) => !v)}
        >
          <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
        </button>
        <FixedMenuPortal
          open={sectionMenuOpen}
          anchorRef={sectionMoreRef}
          preferredWidth={200}
          testId="section-favorites-menu"
          onClose={() => setSectionMenuOpen(false)}
        >
          <MenuItem
            label="View all favorites"
            testId="favorites-view-all-menu"
            onClick={goAll}
          />
        </FixedMenuPortal>
      </div>

      {expanded ? (
        <div className="space-y-0.5" data-testid="favorites-list">
          {isError ? (
            <div
              className="space-y-1 px-3 py-2 text-xs text-slate-500"
              data-testid="favorites-error"
              role="alert"
            >
              <p>Could not load favorites.</p>
              <button
                type="button"
                className="font-medium text-blue-700 hover:underline dark:text-blue-400"
                onClick={() => void refetch()}
              >
                Retry
              </button>
            </div>
          ) : isLoading || (isFetching && list.length === 0) ? (
            <div
              className="px-3 py-2 text-xs text-slate-500"
              data-testid="favorites-loading"
              role="status"
              aria-live="polite"
            >
              Loading favorites…
            </div>
          ) : list.length === 0 ? (
            <p
              className="px-3 py-2 text-xs text-slate-500"
              data-testid="favorites-empty"
              role="status"
            >
              No favorites yet
            </p>
          ) : (
            <>
              {visible.map((fav, index) => (
                <FavoriteRow
                  key={fav.favoriteId}
                  favorite={fav}
                  index={index}
                  total={list.length}
                  allIds={allIds}
                  pending={pending}
                />
              ))}
              {hasMore ? (
                <button
                  type="button"
                  data-testid="favorites-view-all"
                  onClick={goAll}
                  className="w-full rounded-md px-3 py-1.5 text-left text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:hover:bg-slate-800"
                >
                  View all favorites
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
